// 📄 pages/Dashboard/ProjectOrgBanner.tsx
// Sub-fase G2: aviso sutil (no bloqueante) cuando la org activa del
// switcher NO es la dueña del proyecto que se está viendo — p. ej. el
// usuario cambió de org en la topbar mientras seguía navegando dentro de
// un proyecto de otra. El proyecto sigue 100% accesible (las rules ya lo
// permiten); esto es solo para que el usuario entienda por qué "Mis
// Proyectos" ya no lo va a mostrar en la lista si vuelve atrás.
//
// Ajuste posterior: el aviso original asumía que SIEMPRE hay una org
// "correcta" a la que volver — pero un GUEST del proyecto (miembro directo
// invitado a este proyecto puntual, sin ser miembro de la organización
// dueña, mismo concepto que el badge "Guest" de MisProyectos.tsx) nunca va
// a tener esa org en su switcher, con o sin cambiar nada — "Ir a Mis
// Proyectos" no resuelve nada para él, y el mensaje ("no a tu org") suena
// a error cuando en realidad está exactamente donde debería estar. Por
// eso el banner solo se muestra si el usuario SÍ pertenece a la org dueña
// del proyecto (solo que no la tiene activa ahora) — si es un guest puro,
// no se muestra nada.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { Project } from '../../services/projectService.ts';

interface ProjectOrgBannerProps {
  project: Project;
}

export default function ProjectOrgBanner({ project }: ProjectOrgBannerProps) {
  const { orgActual, organizations } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Se resetea cada vez que cambia la org activa — un nuevo cambio de org
  // vuelve a mostrar el banner aunque el anterior se haya cerrado, tal
  // como se pidió ("no reaparece hasta el próximo cambio").
  useEffect(() => {
    setDismissed(false);
  }, [orgActual?.id]);

  // Guest puro (invitado a ESTE proyecto sin ser miembro de la org dueña):
  // esa org nunca va a estar en su lista, así que el aviso de "cambiaste
  // de org" no aplica — mismo criterio que esGuest() en MisProyectos.tsx.
  const orgDelProyecto = organizations.find(o => o.id === project.orgId);
  const esGuestDelProyecto = !!project.orgId && !orgDelProyecto;

  if (!orgActual || !project.orgId || project.orgId === orgActual.id || dismissed || esGuestDelProyecto) {
    return null;
  }

  const nombreOrgProyecto = orgDelProyecto?.name || 'otra organización';

  return (
    <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2.5 mb-4 text-sm text-amber-200">
      <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-400" />
      <p className="flex-1">
        Este proyecto pertenece a <strong>{nombreOrgProyecto}</strong>, no a <strong>{orgActual.name}</strong>.{' '}
        <button
          onClick={() => navigate('/proyectos')}
          className="underline hover:text-amber-100 transition-colors"
        >
          Ir a Mis Proyectos
        </button>
      </p>
      <button
        onClick={() => setDismissed(true)}
        title="Cerrar aviso"
        className="flex-shrink-0 text-amber-400/70 hover:text-amber-200 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
